import { useNavigate } from 'react-router-dom';
import { cn } from '@ponzinomics/ui';
import type { PartnerConfig } from '../../lib/integrations/types';

const CUT_CORNER =
  'polygon(16px 0, 100% 0, 100% calc(100% - 16px), calc(100% - 16px) 100%, 0 100%, 0 16px)';

interface PartnerCardProps {
  partner: PartnerConfig;
  className?: string;
}

export function PartnerCard({ partner, className }: PartnerCardProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (partner.status === 'coming-soon') return;
    if (partner.externalUrl) {
      window.location.href = partner.externalUrl;
      return;
    }
    navigate(`/integrations/${partner.id}`);
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        'group relative overflow-hidden p-5',
        'border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm',
        'transition-all duration-300',
        partner.status === 'live'
          ? 'cursor-pointer hover:-translate-y-0.5 hover:border-white/[0.12] hover:bg-white/[0.05] hover:shadow-[0_8px_30px_rgba(0,0,0,0.3)]'
          : 'cursor-default opacity-50',
        className
      )}
      style={{ clipPath: CUT_CORNER }}
    >
      {/* Top accent line */}
      <div
        className="absolute left-4 right-4 top-0 h-[1px] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        style={{ background: `linear-gradient(90deg, transparent, ${partner.color}, transparent)` }}
      />

      {/* Corner glow */}
      <div
        className="absolute -right-10 -top-10 h-24 w-24 rounded-full opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-20"
        style={{ background: partner.color }}
      />

      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div
          className="flex h-9 w-9 items-center justify-center text-sm font-bold"
          style={{
            backgroundColor: `${partner.color}12`,
            color: partner.color,
            clipPath:
              'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)',
          }}
        >
          {partner.name.charAt(0)}
        </div>
        <span
          className={cn(
            'px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.15em]',
            partner.status === 'live'
              ? 'border border-[#c4f70e]/20 bg-[#c4f70e]/[0.08] text-[#c4f70e]'
              : 'border border-white/[0.06] bg-white/[0.03] text-gray-500'
          )}
          style={{
            clipPath:
              'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
          }}
        >
          {partner.status === 'live' ? 'Live' : 'Coming Soon'}
        </span>
      </div>

      {/* Content */}
      <h3 className="mb-1.5 text-sm font-medium text-white">{partner.name}</h3>
      <p className="line-clamp-2 text-xs leading-relaxed text-gray-500">{partner.description}</p>

      {/* Bottom accent */}
      <div
        className="mt-4 h-[1px] opacity-20"
        style={{ background: `linear-gradient(90deg, ${partner.color}, transparent 60%)` }}
      />
    </div>
  );
}
